/**
 * Retry a promise-returning function with exponential backoff
 * @param {Function} fn - Function that returns a promise
 * @param {Object} options - Retry options
 * @param {number} options.retries - Number of retry attempts (default: 3)
 * @param {number} options.delay - Initial delay in ms (default: 1000)
 * @param {number} options.backoff - Backoff multiplier (default: 2)
 * @param {Function} options.onRetry - Callback on retry (error, attempt)
 * @returns {Promise} - Resolves with the result or rejects after all retries
 */
export function retry(fn, options = {}) {
	const { retries = 3, delay = 1000, backoff = 2, onRetry } = options;

	return new Promise((resolve, reject) => {
		let attempt = 0;

		const execute = () => {
			fn()
				.then(resolve)
				.catch(error => {
					attempt++;
					if (attempt > retries) {
						reject(error);
						return;
					}

					if (onRetry) {
						onRetry(error, attempt);
					}

					const waitTime = delay * Math.pow(backoff, attempt - 1);
					setTimeout(execute, waitTime);
				});
		};

		execute();
	});
}

export default { retry };
